import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Chip,
  Link,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useTranslation } from 'react-i18next';
import type { ReactElement } from 'react';
import { organizationService, type OrganizationMember } from '@services/organizationService';
import { teamService, type Team, type TeamMember } from '@services/teamService';
import { useAuthStore } from '@store/index';
import { useBreadcrumbLabel } from '@store/breadcrumbLabels';
import { toDisplayError } from '../utils/apiError';
import ScrollableTableContainer from '@components/common/ScrollableTableContainer';
import { formatDate as formatDate0 } from '@utils/format';

type TeamRole = 'member' | 'lead';

/** Roles that may manage teams org-wide (mirrors teamService.canManageTeams). */
const MANAGING_ROLES = ['owner', 'admin', 'manager'];

/** The API's max page size for member listings; its default is only 20. */
const ROSTER_LIMIT = 100;

/**
 * Wrap in a Tooltip ONLY when there is a reason to explain. MUI stamps
 * aria-label="" on the child when `title` is empty, which trips ARIA-18/35 —
 * caught by this page's own afixt-engine gate.
 */
function withReason(reason: string, child: ReactElement) {
  if (!reason) return child;
  return (
    <Tooltip title={reason}>
      <span>{child}</span>
    </Tooltip>
  );
}

/** The breadcrumb path for a team page — the crumb whose label it publishes. */
const teamCrumbPath = (orgSlug: string, teamSlug: string) => `/orgs/${orgSlug}/teams/${teamSlug}`;

function TeamDetailPage() {
  const { t } = useTranslation();
  const { slug, teamSlug } = useParams<{ slug: string; teamSlug: string }>();
  const currentUser = useAuthStore((s) => s.user);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [orgId, setOrgId] = useState<string | null>(null);
  // REV-939: the breadcrumb shows the team's name, not its title-cased slug.
  // `teamCrumbFor` is the crumb path `team` was loaded for: this page stays
  // mounted when only :teamSlug changes, and until the next team loads `team`
  // is the previous one, whose name must not land on the new crumb.
  const [teamCrumbFor, setTeamCrumbFor] = useState<string | null>(null);
  useBreadcrumbLabel(
    slug && teamSlug ? teamCrumbPath(slug, teamSlug) : undefined,
    team && teamCrumbFor ? { path: teamCrumbFor, label: team.name } : null
  );
  // The caller's own role in this org, returned by getBySlug. Drives which
  // mutating actions are offered (REV-523).
  const [callerRole, setCallerRole] = useState<string | null>(null);

  const [orgMembers, setOrgMembers] = useState<OrganizationMember[]>([]);
  // Distinguish "roster failed to load" from "no eligible candidates" — the two
  // are NOT the same thing, and conflating them told the user a confident
  // falsehood ("everyone is already on this team").
  const [rosterError, setRosterError] = useState(false);
  // A Retry is in flight. Neither the failure nor the picker is shown, and
  // above all not "everyone is already on this team": the roster is empty
  // while it loads (REV-988).
  const [rosterRetrying, setRosterRetrying] = useState(false);
  // Where focus goes once a retry has put the dialog's body back, because the
  // Retry button that held it is gone (REV-988).
  const pickerRef = useRef<{ focus: () => void } | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [focusAfterRetry, setFocusAfterRetry] = useState(false);

  const [feedback, setFeedback] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState<TeamRole>('member');
  const [adding, setAdding] = useState(false);
  // Dialog-scoped error. The page-level alert sits OUTSIDE the modal, which MUI
  // marks aria-hidden while open — an error rendered there is unreachable to
  // assistive tech and hidden behind the scrim.
  const [addError, setAddError] = useState<string | null>(null);

  const [actionMenu, setActionMenu] = useState<{
    anchorEl: HTMLElement | null;
    member: TeamMember | null;
  }>({ anchorEl: null, member: null });
  const [confirmRemove, setConfirmRemove] = useState<TeamMember | null>(null);
  // REV-982: a failed removal is reported INSIDE the confirm dialog, which
  // stays open: the page behind a modal is aria-hidden until the modal has
  // finished closing, so a failure announced there would go unheard.
  const [removeError, setRemoveError] = useState<string | null>(null);
  // A success to announce once a dialog has finished closing, for the same
  // reason: before then the page's live region is still aria-hidden.
  const afterDialogExit = useRef<string | null>(null);
  const announceAfterDialogExit = useCallback(() => {
    const message = afterDialogExit.current;
    afterDialogExit.current = null;
    if (message) setFeedback({ severity: 'success', message });
  }, []);
  // Scope the busy state to the row being mutated rather than disabling every
  // row's control at once.
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  // Removing a member unmounts the row whose button the confirm dialog would
  // restore focus to, dropping focus onto <body>. Park it somewhere stable.
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const loadRoster = useCallback(async (resolvedOrgId: string): Promise<boolean> => {
    try {
      const page = await organizationService.getMembers(resolvedOrgId, { limit: ROSTER_LIMIT });
      setOrgMembers(page.items ?? []);
      setRosterError(false);
      return true;
    } catch {
      setOrgMembers([]);
      setRosterError(true);
      return false;
    }
  }, []);

  const retryRoster = useCallback(async () => {
    if (!orgId || rosterRetrying) return;
    // Clear the failure message BEFORE asking again. The alert region's text
    // would be identical if the retry fails the same way, and a live region
    // whose content does not change is not re-announced; emptied first, the
    // repeat failure is a fresh insertion (REV-988). The Retry button is not
    // part of that: it stays put, so focus does.
    setRosterError(false);
    setRosterRetrying(true);
    const ok = await loadRoster(orgId);
    setRosterRetrying(false);
    setFocusAfterRetry(ok);
  }, [orgId, rosterRetrying, loadRoster]);

  // The Retry button unmounts on success; left alone, MUI's focus trap drops
  // focus on the dialog container. Put it on the first control of what
  // replaced it, or on Cancel when there is nobody left to add.
  useEffect(() => {
    if (!focusAfterRetry) return;
    setFocusAfterRetry(false);
    (pickerRef.current ?? cancelRef.current)?.focus();
  }, [focusAfterRetry]);

  const load = useCallback(async () => {
    if (!slug || !teamSlug) return;
    setIsLoading(true);
    setError(null);
    try {
      const org = await organizationService.getBySlug(slug);
      // Resolve the team from its slug first; member sub-resources are keyed by
      // the team's UUID, so fetch the roster with the resolved id.
      const teamResult = await teamService.getById(org.id, teamSlug);
      // Explicit limit: the API defaults to 20, which would truncate the roster
      // AND make the eligible-pool filter offer people already on the team
      // (they'd 409 on submit).
      const membersResult = await teamService.getMembers(org.id, teamResult.id, {
        limit: ROSTER_LIMIT,
      });
      setOrgId(org.id);
      setCallerRole(org.role);
      setTeam(teamResult);
      setTeamCrumbFor(teamCrumbPath(slug, teamSlug));
      setMembers(membersResult.items ?? []);
      // Secondary: only feeds the add-member picker, so it must not break the
      // page — but its failure is surfaced, not silently swallowed.
      await loadRoster(org.id);
    } catch {
      setError(t('teams.detail.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [slug, teamSlug, t, loadRoster]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshMembers = useCallback(async () => {
    if (!orgId || !team) return;
    const result = await teamService.getMembers(orgId, team.id, { limit: ROSTER_LIMIT });
    setMembers(result.items ?? []);
  }, [orgId, team]);

  const eligible = useMemo(() => {
    const onTeam = new Set(members.map((m) => m.user_id));
    return orgMembers.filter((om) => !onTeam.has(om.user_id));
  }, [orgMembers, members]);

  const nameOf = useCallback(
    (m: TeamMember) =>
      m.user
        ? `${m.user.first_name} ${m.user.last_name}`.trim()
        : t('teams.members.unknownUser', 'Unknown user'),
    [t]
  );

  /**
   * A failure that names what failed, then why. The API's reason alone
   * ("User is not a member of this team") does not say which action, or which
   * row, it is about once focus has left the row (REV-982).
   */
  const failureFor = (what: string, err: unknown) => {
    const reason = toDisplayError(err, '');
    return reason ? `${what} ${reason}` : what;
  };

  // Permission model mirrors revenant-api teamService:
  //   add    → owner/admin/manager OR this team's lead
  //   role   → owner/admin/manager ONLY (a team lead may NOT re-role)
  //   remove → owner/admin/manager OR team lead OR yourself
  const canManage = Boolean(callerRole && MANAGING_ROLES.includes(callerRole));
  const isTeamLead = useMemo(
    () => members.some((m) => m.user_id === currentUser?.id && m.role === 'lead'),
    [members, currentUser]
  );
  const canAdd = canManage || isTeamLead;
  const canChangeRole = canManage;
  const canRemove = useCallback(
    (m: TeamMember) => canManage || isTeamLead || m.user_id === currentUser?.id,
    [canManage, isTeamLead, currentUser]
  );

  const handleAdd = async () => {
    if (!orgId || !team || adding) return;
    if (!addUserId) {
      // Validate on submit rather than pre-disabling the button — and show the
      // message INSIDE the dialog so it is actually perceivable.
      setAddError(t('teams.members.selectSomeone', 'Choose an organization member to add.'));
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await teamService.addMember(orgId, team.id, { user_id: addUserId, role: addRole });
      await refreshMembers();
      setAddOpen(false);
      setAddUserId('');
      setAddRole('member');
      afterDialogExit.current = t('teams.members.addSuccess', 'Member added to the team.');
    } catch (err) {
      setAddError(toDisplayError(err, t('teams.members.addFailed')));
    } finally {
      setAdding(false);
    }
  };

  const handleChangeRole = async (member: TeamMember) => {
    if (!orgId || !team) return;
    const next: TeamRole = member.role === 'lead' ? 'member' : 'lead';
    setActionMenu({ anchorEl: null, member: null });
    setBusyUserId(member.user_id);
    setFeedback(null);
    try {
      await teamService.updateMemberRole(orgId, team.id, member.user_id, next);
      await refreshMembers();
      setFeedback({
        severity: 'success',
        message: t('teams.members.roleChanged', 'Role updated.'),
      });
    } catch (err) {
      setFeedback({
        severity: 'error',
        message: failureFor(t('teams.members.roleChangeFailed', { name: nameOf(member) }), err),
      });
    } finally {
      setBusyUserId(null);
    }
  };

  const removing = Boolean(confirmRemove && busyUserId === confirmRemove.user_id);

  const handleRemove = async () => {
    if (!orgId || !team || !confirmRemove || removing) return;
    setBusyUserId(confirmRemove.user_id);
    setFeedback(null);
    setRemoveError(null);
    try {
      await teamService.removeMember(orgId, team.id, confirmRemove.user_id);
      await refreshMembers();
      afterDialogExit.current = t('teams.members.removed', 'Member removed from the team.');
      setConfirmRemove(null);
      // The row that owned focus is gone; restore it deliberately rather than
      // letting it fall to <body>.
      setTimeout(() => addButtonRef.current?.focus(), 0);
    } catch (err) {
      // The dialog stays open and keeps focus, so the user can retry or cancel.
      setRemoveError(
        failureFor(t('teams.members.removeFailed', { name: nameOf(confirmRemove) }), err)
      );
    } finally {
      setBusyUserId(null);
    }
  };

  const formatDate = (iso: string | null | undefined) => {
    if (!iso) return '—';
    return formatDate0(iso, { fallback: String(iso) });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* REV-575: back to the TEAMS LIST, not the organization root. This is a
          team's member roster; the list of teams is where the user came from
          and where "back" means. /orgs/:slug/* renders the org page with the
          tab taken from the path, so /teams lands on the Teams tab. */}
      <Link
        component={RouterLink}
        to={slug ? `/orgs/${slug}/teams` : '/orgs'}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2 }}
      >
        <ArrowBackIcon fontSize="small" />
        {t('teams.detail.backToTeams', 'Back to teams')}
      </Link>

      {/* REV-982: two live regions, mounted from the first render so each
          already exists when its content changes (a region mounted and filled
          in the same commit is often not announced). A success is polite; a
          failure, including the page failing to load, is assertive. Each
          Alert inside is role="none": MUI gives every Alert role="alert", and
          a nested alert would make a success assertive too and announce a
          failure twice. */}
      <Box role="status">
        {feedback?.severity === 'success' && (
          <Alert severity="success" role="none" sx={{ mb: 3 }} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}
      </Box>
      <Box role="alert">
        {error && (
          <Alert severity="error" role="none" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {feedback?.severity === 'error' && (
          <Alert severity="error" role="none" sx={{ mb: 3 }} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress size={24} aria-hidden />
          <Typography>{t('teams.detail.loading')}</Typography>
        </Box>
      ) : !team ? (
        !error && <Typography>{t('teams.detail.notFound')}</Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="h4" component="h1" fontWeight={700}>
              {team.name}
            </Typography>
            <Chip
              label={
                team.status === 'archived'
                  ? t('teams.detail.statusArchived')
                  : t('teams.detail.statusActive')
              }
              color={team.status === 'archived' ? 'default' : 'success'}
              size="small"
            />
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {team.description || t('teams.detail.noDescription')}
          </Typography>

          <Paper variant="outlined" sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="h6" component="h2" id="team-members-heading">
                {t('teams.detail.members')}
              </Typography>
              {/* Visible-but-disabled with an explanation rather than hidden,
                  matching the REV-450 pattern in MembersTab. */}
              {withReason(
                canAdd ? '' : t('teams.members.noPermissionAdd'),
                <Button
                  ref={addButtonRef}
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => {
                    setAddError(null);
                    setAddOpen(true);
                  }}
                  disabled={!canAdd}
                >
                  {t('teams.members.add')}
                </Button>
              )}
            </Box>

            {members.length === 0 ? (
              <Typography color="text.secondary">{t('teams.members.noMembers')}</Typography>
            ) : (
              <ScrollableTableContainer>
                <Table aria-labelledby="team-members-heading">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('teams.detail.memberName')}</TableCell>
                      <TableCell>{t('teams.detail.memberEmail')}</TableCell>
                      <TableCell>{t('teams.members.role')}</TableCell>
                      <TableCell>{t('teams.members.joinedAt')}</TableCell>
                      <TableCell>{t('teams.members.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {members.map((m) => {
                      // Keyed by user_id: user_teams has a composite PK and the
                      // API returns no membership id (REV-515).
                      const actionable = canChangeRole || canRemove(m);
                      const rowBusy = busyUserId === m.user_id;
                      return (
                        <TableRow key={m.user_id}>
                          <TableCell>{nameOf(m)}</TableCell>
                          <TableCell>{m.user?.email ?? '—'}</TableCell>
                          <TableCell>
                            <Chip
                              label={t(`teams.roles.${m.role}`)}
                              size="small"
                              color={m.role === 'lead' ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{formatDate(m.joined_at)}</TableCell>
                          <TableCell>
                            {withReason(
                              actionable ? '' : t('teams.members.noPermissionManage'),
                              <IconButton
                                onClick={(e) => {
                                  if (rowBusy) return;
                                  setActionMenu({ anchorEl: e.currentTarget, member: m });
                                }}
                                // REV-987: aria-disabled, not disabled, while
                                // this row's mutation is in flight. The menu
                                // returns focus here when it closes, and a
                                // disabled button drops it to <body>.
                                disabled={!actionable}
                                aria-disabled={rowBusy || undefined}
                                aria-busy={rowBusy || undefined}
                                aria-haspopup="true"
                                aria-expanded={actionMenu.member?.user_id === m.user_id}
                                // Per-row name: a bare "Actions" repeated down
                                // the table is useless to a screen reader.
                                aria-label={t('teams.members.actionsFor', {
                                  defaultValue: 'Actions for {{name}}',
                                  name: nameOf(m),
                                })}
                              >
                                {rowBusy ? (
                                  <CircularProgress size={20} color="inherit" aria-hidden />
                                ) : (
                                  <MoreVertIcon />
                                )}
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollableTableContainer>
            )}
          </Paper>
        </>
      )}

      <Menu
        anchorEl={actionMenu.anchorEl}
        open={Boolean(actionMenu.anchorEl)}
        onClose={() => setActionMenu({ anchorEl: null, member: null })}
        MenuListProps={{ 'aria-label': t('teams.members.actions') }}
      >
        {/* A team lead may add and remove, but the API forbids them re-roling. */}
        {withReason(
          canChangeRole ? '' : t('teams.members.noPermissionRole'),
          <MenuItem
            onClick={() => actionMenu.member && handleChangeRole(actionMenu.member)}
            disabled={!canChangeRole}
          >
            <ListItemIcon>
              <SwapHorizIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {actionMenu.member?.role === 'lead'
                ? t('teams.members.makeMember', 'Make member')
                : t('teams.members.makeLead', 'Make team lead')}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setRemoveError(null);
            setConfirmRemove(actionMenu.member);
            setActionMenu({ anchorEl: null, member: null });
          }}
          disabled={Boolean(actionMenu.member && !canRemove(actionMenu.member))}
        >
          <ListItemIcon>
            <PersonRemoveIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('teams.members.remove')}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={{ transition: { onExited: announceAfterDialogExit } }}
      >
        <DialogTitle>{t('teams.members.addTitle', 'Add a team member')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t(
              'teams.members.addHelp',
              'Only people who already belong to this organization can join a team.'
            )}
          </DialogContentText>

          {/* Scoped to the dialog: the page-level alert lives in the subtree MUI
              marks aria-hidden while a modal is open, so an error rendered there
              is unreachable to AT and hidden behind the scrim. One region,
              mounted with the dialog so it exists before an error arrives, and
              role="none" inside it so no alert is nested in another (REV-987). */}
          <Box role="alert">
            {addError && (
              <Alert severity="error" role="none" sx={{ mb: 2 }}>
                {addError}
              </Alert>
            )}
            {(rosterError || rosterRetrying) && (
              <Alert
                severity="error"
                role="none"
                action={
                  // Stays mounted, and focused, through the retry: unmounting
                  // it would drop focus to the dialog. It is aria-disabled
                  // while in flight, not disabled, for the same reason as the
                  // Add button (REV-987); retryRoster ignores a repeat press.
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => void retryRoster()}
                    aria-disabled={rosterRetrying || undefined}
                    aria-busy={rosterRetrying || undefined}
                  >
                    {t('common.retry')}
                  </Button>
                }
              >
                {/* Only the message is emptied while retrying, so an identical
                    repeat failure is a fresh insertion (REV-988). */}
                {rosterError ? t('teams.members.rosterFailed') : null}
              </Alert>
            )}
          </Box>

          {rosterError || rosterRetrying ? null : eligible.length === 0 ? (
            // role="status": MUI gives every Alert role="alert", which would
            // announce this informational state assertively. It is not an
            // error and needs no interruption; polite is the right register.
            <Alert severity="info" role="status">
              {t(
                'teams.members.noEligible',
                'Everyone in this organization is already on this team.'
              )}
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth error={Boolean(addError && !addUserId)}>
                <InputLabel id="team-add-user-label">
                  {t('teams.members.selectUser', 'Organization member')}
                </InputLabel>
                <Select
                  labelId="team-add-user-label"
                  id="team-add-user"
                  inputRef={pickerRef}
                  value={addUserId}
                  label={t('teams.members.selectUser', 'Organization member')}
                  onChange={(e: SelectChangeEvent) => {
                    setAddUserId(e.target.value);
                    setAddError(null);
                  }}
                  aria-describedby="team-add-user-help"
                >
                  {eligible.map((om) => (
                    <MenuItem key={om.user_id} value={om.user_id}>
                      {`${om.user.first_name} ${om.user.last_name}`.trim()} ({om.user.email})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText id="team-add-user-help">
                  {t('teams.members.selectUserHelp', 'Pick who should join this team.')}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="team-add-role-label">{t('teams.members.role')}</InputLabel>
                <Select
                  labelId="team-add-role-label"
                  id="team-add-role"
                  value={addRole}
                  label={t('teams.members.role')}
                  onChange={(e: SelectChangeEvent) => setAddRole(e.target.value as TeamRole)}
                >
                  <MenuItem value="member">{t('teams.roles.member')}</MenuItem>
                  <MenuItem value="lead">{t('teams.roles.lead')}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            ref={cancelRef}
            variant="outlined"
            onClick={() => setAddOpen(false)}
            disabled={adding}
          >
            {t('common.cancel')}
          </Button>
          {!rosterError && !rosterRetrying && eligible.length > 0 && (
            <Button
              variant="contained"
              onClick={handleAdd}
              // Never pre-disabled. While the request is in flight it is
              // aria-disabled, not disabled: disabling the focused button
              // drops focus to <body>, so the failure would be announced to a
              // user who has lost their place (REV-987, as REV-982 did for
              // Remove). handleAdd ignores a repeat activation.
              aria-disabled={adding || undefined}
              aria-busy={adding || undefined}
              startIcon={
                adding ? <CircularProgress size={16} color="inherit" aria-hidden /> : undefined
              }
            >
              {adding
                ? t('teams.members.adding', 'Adding…')
                : t('teams.members.addSubmit', 'Add to team')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(confirmRemove)}
        onClose={() => setConfirmRemove(null)}
        slotProps={{ transition: { onExited: announceAfterDialogExit } }}
      >
        <DialogTitle>{t('teams.members.remove')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('teams.members.confirmRemove', {
              name: confirmRemove ? nameOf(confirmRemove) : '',
            })}
          </DialogContentText>
          {/* Mounted with the dialog, before anything fails: see the page's
              live regions above. */}
          <Box role="alert">
            {removeError && (
              <Alert severity="error" role="none" sx={{ mt: 2 }}>
                {removeError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setConfirmRemove(null)}
            disabled={Boolean(busyUserId)}
          >
            {t('common.cancel')}
          </Button>
          {/* Label the action explicitly rather than a generic "Delete".
              aria-disabled, not disabled, while the request is in flight:
              disabling the focused button drops focus to <body>, and a failure
              is then announced to a user who has lost their place (REV-982). */}
          <Button
            color="error"
            variant="contained"
            onClick={handleRemove}
            aria-disabled={removing || undefined}
            aria-busy={removing || undefined}
            startIcon={
              removing ? <CircularProgress size={16} color="inherit" aria-hidden /> : undefined
            }
          >
            {removing ? t('teams.members.removing', 'Removing…') : t('teams.members.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default TeamDetailPage;
